/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2014, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'jquery',
	'underscore',
	'../VisView',
	'../../../lib/@waca/core-client/js/core-client/utils/dom-utils',
	'../../../lib/@waca/dashboard-common/dist/utils/ScrollBarUtil',
	'../../../lib/@waca/core-client/js/core-client/i18n/FormatResources',
	'../../../util/DashboardFormatter',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/utils/EventChainLocal',
	'../VisEventHandler',
	'./IndentedListEventTarget',
	'../common/PrintHelper',
	'../../../util/EventUtils'
], function(
	$,
	_,
	VisView,
	domUtils,
	ScrollBarUtil,
	FormatResources,
	Formatter,
	stringResources,
	EventChainLocal,
	VisEventHandler,
	IndentedListEventTarget,
	PrintHelper,
	EventUtils
) {
	'use strict';

	const DEFAULT_MAX_WIDTH = 320;
	const DEFAULT_MAX_HEIGHT = 480;
	const GROUP_MAX_LENGTH  = 100;
	const DELAYLOADING_THROTTLE = 2000;
	const ATTRIBUTES = {
		COLUMN_CLASS: 'col',
		ROW_CLASS: 'row'
	};
	// Maps related to changing text properties
	// Map properties to css style strings
	const PROPERTY_TO_STYLE = {
		'font-weight': 'bold',
		'font-style': 'italic',
		'text-decoration': 'underline'
	};

	const PROPERTY_MAP = {
		listValueColor: 'color',
		listValueFontSize: 'font-size',
		listValueFontFace: 'font-family',
		listValueFontBold: 'font-weight',
		listValueFontItalic: 'font-style',
		listValueFontUnderline: 'text-decoration',
		listValueFontAlign: 'text-align'
	};

	const JUSTIFY_CONTENT_MAP = {
		Right: 'flex-end',
		Left: 'flex-start',
		Center: 'center'
	};

	const INDENT_MAP = {
		NO_SPACE: 0,
		FIRST_INDENT: 1,
		SPACE_XS: 8,
		SPACE_S: 16
	};

	const IndentedListView  = VisView.extend({
		templateString: '<div role="tree" aria	-expanded="true" aria-label="' + _.escape(stringResources.get('srILVTreeLabel')) + '" tabIndex="-1" class="datawidget indentedlist-content userPrimaryFGPaletteColor"  data-attach-point="contentNode"></div>',
		events: {
			'keydown .item': 'onDataItemKeydown',
			'mousedown': 'onMouseDown'
		},

		init: function () {
			IndentedListView.inherited('init', this, arguments);
			this._cache = []; /*Holds all items after a render*/
			this._groupArrays = []; /*Holds all items after a 3*/
			this._groupCounter = 0;
			this._focusedNode = null;
			this.initialRender = true;
			this._groupArrays = [];
			this._groupCounter = 0;


			this.$el.addClass('dataview indentedlist-view indentedlist-view-initial-max-size');
			this.initialRender = true;

			var $inner = this.$el.find('.indentedlist-content').first();
			this.scrollBarUtil = new ScrollBarUtil(
				$inner, // inner
				this.$el // outer
			);
			this.isMobilePannable = true;
			this.content = this.content || this.dashboardApi.getCanvas().getContent(this.ownerWidget.getId());
			this._scrollHandlerFn = this.scrollHandler.bind(this);
			this.icons = this.dashboardApi.getFeature('Icons');
			this.colors = this.dashboardApi.getFeature('Colors');
		},

		scrollHandler: function (evt) {
			var $dom = $(evt.target);
			if ($dom.scrollTop() + $dom.innerHeight() >= $dom[0].scrollHeight - 20) {
				this._addMore();
			}
		},

		onMouseDown: function (event) {
			if (this._isOnScrollBar(event)) {
				event.stopPropagation();
			}
		},

		getRenderer: function () {
			return 'dashboard-analytics/visualizations/renderer/indentedlist/IndentedListRenderer';
		},

		/**
		 * Loads and creates the control. Returns a promise which is resolved when the control
		 * is created and ready to render
		 */
		initialize: function() {
			let result;
			if (this.visControl) {
				result = Promise.resolve(this.visControl);
			} else {
				result = new Promise((resolve, reject) => {
					try {
						require([this.content.getFeature('Visualization').getDefinition().getProperty('control')], (VisControl) => {
							this.visControl = new VisControl({
								domNode: this.$el.find('.indentedlist-content')[0]
							});
							resolve(this.visControl);
						}, reject);
					} catch(error) {
						reject(error);
					}
				}).then(() => {
					this.eventHandler = new VisEventHandler({
						target: new IndentedListEventTarget({
							$el: this.$el,
							visControl: this.visControl,
							attributes: JSON.parse(JSON.stringify(ATTRIBUTES)),
							logger: this.logger
						}),
						dashboardSettings: this.dashboardSettings,
						transaction: this.transactionApi,
						logger: this.logger,
						ownerWidget: this.ownerWidget,
						visAPI: this.ownerWidget.visAPI,
						edgeSelection: true
					});
					return this.visControl;
				});
			}

			return result;
		},

		/*
		 * Set a list widget's size based on its content in order to set an initial size
		 */
		_clampDefaultDimensions: function () {
			//indentedlist-view-initial-max-size is applied when a list widget is first created to help determine a reasonable default size.
			//It turns off line wrapping. This is to compensate for a browser behaviour which artificially reduces available space to a list widget when it
			//is on a templated page outside the main area (see defect 49563).
			//This function determines the best default size of a list widget and hard codes it in.

			if (this.$el.hasClass('indentedlist-view-initial-max-size')) {
				if (this.$el.width() > DEFAULT_MAX_WIDTH) {
					//Clamp max default width
					this.$el.width(DEFAULT_MAX_WIDTH);
				} else {
					//Set width (to compensate for browser artificially shortening width).
					this.$el.width(this.$el.width());
				}

				//Turn line wrapping back on now that the width is set.
				this.$el.removeClass('indentedlist-view-initial-max-size');

				if (this.$el.height() > DEFAULT_MAX_HEIGHT) {
					//Clamp max height
					this.$el.height(DEFAULT_MAX_HEIGHT);
					//Set width (to compensate for vertical scroll bar).
					var newWidth = this.$el.width() + this._getScrollbarWidth();
					this.$el.width(newWidth);

				}
			}
		},

		/*
		 * Clear the clamping performed in _clampDefaultDimensions.
		 */
		_clearDefaultDimensionClamp: function () {
			this.$el.width('');
			this.$el.height('');
		},

		_getScrollbarWidth: function () {
			var $content = this.$el.find('.datawidget');
			var scrollbarWidth = $content.width() - $content[0].scrollWidth;
			return scrollbarWidth;
		},

		/**
		 * @param {Object} renderInfo - renderInfo passed to all render methods from the render sequence.
		 * @returns a Promise which is resolved when rendering is complete.
		 */
		render: function (renderInfo) {
			this._cache = [];
			this._groupArrays = [];
			this._groupCounter = 0;
			this._focusedNode = null;
			if (!this.isMappingComplete() || this.hasMissingFilters() || this.hasUnavailableMetadataColumns()) {
				this.$el.find('.indentedlist-content').empty();
				this.resizeToWidget(renderInfo);
				this.initialRender = true; // Reset flag to calculate width
				this.renderIconView();
				return Promise.resolve(this);
			}

			this._setColumnIdToDataItemMap();

			this.onResultsReady(renderInfo.data.getResult());

			this.removeIconView();
			this.resizeToWidget(renderInfo);
			this.renderSelected();

			if (this.initialRender) {
				this.initialRender = false;
				this.visModel.ownerWidget.whenContainerIsReady.promise
					.then(() => {
						this._clampDefaultDimensions();
						this._clearDefaultDimensionClamp();
					});
			}

			return IndentedListView.inherited('render', this, arguments);
		},

		//Load a map of columnIds to dataItemAPI's for use in selection API's.
		_setColumnIdToDataItemMap() {
			this.columnIdToDataItemMap={};
			let slots = this.content.getFeature('Visualization').getSlots();
			slots && slots.getMappingInfoList().forEach((mappingInfo) => {
				this.columnIdToDataItemMap[mappingInfo.dataItem.getColumnId()] = mappingInfo.dataItem;
			});
		},

		remove: function() {
			this.scrollBarUtil = null;
			if (this.eventHandler) {
				this.eventHandler.remove();
				this.eventHandler = null;
			}
			if (this._cache) {
				this._cache = null;
			}
			IndentedListView.inherited('remove', this, arguments);
		},

		getDescription: function () {
			// Append the F12 key instruction to the description
			var description = IndentedListView.inherited('getDescription', this, arguments);
			return stringResources.get('WidgetLabelWithDescripion', {
				label: description,
				description: stringResources.get('f12KeyDescription')
			});
		},

		renderIconView: function () {
			// Remove the size restriction for an indented list's icon view
			this.$el.removeClass('indentedlist-view-initial-max-size');
			IndentedListView.inherited('renderIconView', this, arguments);
		},



		/**
		 * Divide the original array of view HTML markups Strings into multiple arrays
		 * each array has an maximum length of GROUP_MAX_LENGTH defined in the class
		 **/
		_groupItems: function (origArray) {
			var lists = _.groupBy(origArray, function (element, index) {
				return Math.floor(index / GROUP_MAX_LENGTH);
			}.bind(this));
			return _.toArray(lists); //Added this to convert the returned object to an array.
		},

		_isEqualLevel: function (curTuple, prevTuple) {
			var notSame = _.find(curTuple, function (obj, k) {
				return prevTuple[k] ? prevTuple[k].value !== obj.value : true;
			});
			return notSame ? false : true;
		},


		onResultsReady: function (results) {
			this.maxIndentationLevel = 0;
			let code = [];
			let prevRow = null;
			let indentationLevel = 0;

			let slots = this.content.getFeature('Visualization').getSlots();
			let level1Slot = slots.getSlot('level1');

			let resultRowSize = results.getRowCount();
			let dataItemFormatMap = {};
			const alignment = this.content.getPropertyValue('listValueFontAlign') ? this.content.getPropertyValue('listValueFontAlign') : 'Left';
			_.each(level1Slot.getDataItemList(), (dataItem) => {
				dataItemFormatMap[dataItem.getId()] = dataItem.getFormat();
			});

			for (let rowIndex = 0; rowIndex < resultRowSize; rowIndex++) {
				let row = [];

				_.each(level1Slot.getDataItemList(), (dataItemAPI, colIndex) => {
					let v = [],
						t = {};
					/*By current design, indented list currently does not support stacked data items per data slot
					 *Therefore there will be only one tuple part for one dataItem
					 */
					let dataItem = results.getValue(rowIndex, colIndex)[0];
					let dataItemLabel = dataItem.label;

					if (dataItemAPI) {
						v.push(Formatter.format(dataItemLabel, dataItemFormatMap[dataItemAPI.getId()]));
						t[dataItemAPI.getColumnId()] = dataItem;
					}
					row.push({
						tuple: t,
						value: v.join(FormatResources.getListSeparatorSymbol() + ' '),
						rowId: rowIndex,
						colId: colIndex
					});
				});

				let tuple = {};
				let startLevel = 0;
				if (prevRow) {
					while (startLevel < row.length && this._isEqualLevel(row[startLevel].tuple, prevRow[startLevel].tuple)) {
						_.extend(tuple, row[startLevel].tuple);
						startLevel++;
					}
				}

				if (startLevel < row.length) {
					if (prevRow && startLevel !== prevRow.length - 1) {
						var levels = prevRow.length - startLevel - 1;

						for (var i = 0; i < levels; i++) {
							indentationLevel--;
							code.push('</div>');
						}
					}

					for (var colIdx = startLevel; colIdx < row.length; colIdx++) {
						if (colIdx > 0 && colIdx !== startLevel) {
							indentationLevel++;
							const initialIndent = indentationLevel == INDENT_MAP['FIRST_INDENT'] ? INDENT_MAP['SPACE_XS'] : INDENT_MAP['NO_SPACE'];
							const indentStyle = alignment === 'center' ? '' : `style="padding-${alignment}: ${initialIndent + (indentationLevel * INDENT_MAP['SPACE_S'])}px"`;
							code.push(`<div class="indentGroup" role="group" depth="${indentationLevel}" ${indentStyle}>`);
						}
						_.extend(tuple, row[colIdx].tuple);
						if (this.maxIndentationLevel < indentationLevel) {
							this.maxIndentationLevel = indentationLevel;
						}
						//Store Element Metadata only
						code.push({
							// ensure the clone, so that the child tuple doesn't overwrite the parent tuple
							'tuple': _.clone(tuple),
							'colIdx': colIdx,
							'rowValue': row[colIdx].value,
							'iLevel': indentationLevel,
							'rowId': row[colIdx].rowId,
							'colId': row[colIdx].colId
						});
					}
				}
				prevRow = row;

			}

			for (let k = 0; k < indentationLevel; k++) {
				code.push('</div>');
			}

			delete this.styleStr;
			this.selections = this.getController();
			const fillColor = this.content && this.content.getPropertyValue('fillColor');
			const foregroundColorProperties = this.colors.getForegroundColorPropertiesForUIElement(fillColor, 'tabs');
			this.cellFillColor = fillColor && this.colors.getHexColorFromDashboardColorSet(fillColor);
			this.cellTextColor = foregroundColorProperties &&  foregroundColorProperties.length && foregroundColorProperties[0].value;

			if (this.el) {
				let $listContent = this.$el.find('.indentedlist-content');
				let $scrollInner = $listContent.first();
				$scrollInner.off('scroll', this._scrollHandlerFn);

				const renderFullList = PrintHelper.isRenderFullEnabled(this.dashboardApi, this.content) || (code.length <= DELAYLOADING_THROTTLE);
				if (renderFullList) {
					this._convertListObjectsToHTML(code);
					$listContent.html(code.join(''));

				} else {
					$scrollInner.on('scroll', this._scrollHandlerFn);
					this._cache = code;
					this._addMore(true);
				}
			}
		},

		/**
		 * Append more HTML markups to the existing view
		 * @param {boolean} isNewRender, true, add first group only
		 **/
		_addMore: function(isNewRender) {
			var currentArray = [];
			this._groupArrays = this._groupItems(this._cache);
			//Add only first group for a rerender
			if (isNewRender) {
				currentArray = this._groupArrays[0];
			} else {
				//Add one more group
				if (this._groupArrays.length > this._groupCounter) {
					var nEndPos = this._groupCounter * GROUP_MAX_LENGTH + this._groupArrays[this._groupCounter].length;
					currentArray = this._cache.slice(0, nEndPos);
				} else {
					return;
				}
			}

			this._convertListObjectsToHTML(currentArray);

			var nLen = currentArray.length;
			var sLastEle = currentArray[nLen - 1];

			if (sLastEle !== '</div>') {
				var $LastEle = $(sLastEle);
				var sDepth = $LastEle.attr('depth');
				if (sDepth === '2') {
					currentArray.push('</div></div>');
				} else if (sDepth === '1') {
					currentArray.push('</div>');
				}
			} else {
				var s2ndLastEle = currentArray[nLen - 2];
				if (s2ndLastEle !== '</div>') {
					var $2ndLastEle = $(s2ndLastEle);
					if ($2ndLastEle.attr('depth') === '2') {
						currentArray.push('</div>');
					}
				}
			}
			this.$el.find('.indentedlist-content').empty();
			this.$el.find('.indentedlist-content').html(currentArray.join(''));
			this._groupCounter++;
			this.renderSelected();
		},

		/*@param {Array} objects*/
		_convertListObjectsToHTML: function(objects){
			let oCurElement;
			const styleString = this._constructStyleString();
			for(var i = 0; i< objects.length; i++){
				oCurElement = objects[i];
				if(oCurElement && oCurElement.rowValue){
					oCurElement.style = styleString;
					objects[i] = this.renderItem(oCurElement);
				}
			}
		},

		_constructStyleString: function () {
			if (!this.styleStr) {
				this.styleStr = 'style="';
				_.each(PROPERTY_MAP, (styleName, propertyName) => {
					let propertyValue = this.content.getPropertyValue(propertyName);
					if (propertyValue) {
						const value = PROPERTY_TO_STYLE[styleName];
						if(value) {
							propertyValue = PROPERTY_TO_STYLE[styleName];
						}
						this.styleStr = this.styleStr + styleName + ':' + propertyValue + ';';
					}
				});
				this.styleStr += '"';
			}
			return this.styleStr;
		},

		_getContentJustificationStyle: function(){
			const textAlignment = this.content.getPropertyValue('listValueFontAlign');
			const contentJustification = {};
			if(textAlignment){
				contentJustification['justify-content'] = `${JUSTIFY_CONTENT_MAP[textAlignment]};`;
			}
			return contentJustification;
		},

		_getItemElementStyle (styleObject) {
			let style = '';
			if (styleObject) {
				const keys = Object.keys(styleObject);
				if (keys.length) {
					style = ' style="';
					keys.forEach(key => {
						style = `${style}${key}:${styleObject[key]}`;
					});
					style += '"';
				}
			}
			return style;
		},

		renderItem: function (item) {
			var tuple, label, indentationLevel, colId, rowId, styleString;
			let textColor = '';
			if(item){
				tuple= item.tuple;
				label = item.rowValue;
				indentationLevel = item.iLevel;
				colId = item.colId;
				rowId = item.rowId;
				styleString = item.style;
			}

			if(this.content.getPropertyValue('listValueColor')){
				textColor = `style="color: ${this.content.getPropertyValue('listValueColor')};"`;
			}

			if(!label){
				return;
			}
			const icon = indentationLevel < this.maxIndentationLevel ? `<div class="list-svg-container"> \
							<svg class="svgIcon" xmlns:xlink="http://www.w3.org/1999/xlink" focusable="false">\
								<use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#${this.icons.getIcon('caretDown32').id}"></use>\
							</svg>\
						</div>`: '';
			const isSelected = this.isSubsetOfTupleSelected(tuple, this.selections);
			const itemElementStyleObject = this._getContentJustificationStyle();
			// background color from Property
			if (this.cellFillColor) {
				itemElementStyleObject.color = `${this.cellTextColor};`;
				if (!isSelected) {
					itemElementStyleObject['background-color'] = `${this.cellFillColor};`;
				}
			}
			const contentJustificationStyle = this._getItemElementStyle(itemElementStyleObject);
			const itemClass = 'class="item' + (isSelected ? ' selected' : '') + '"';
			return [
				'<div '+ itemClass + contentJustificationStyle+' role="treeitem" tabindex="-1" aria-label="', _.escape(label), '" data-tuple="',  encodeURIComponent(JSON.stringify(tuple)), '" depth="', indentationLevel,
				'" ', ATTRIBUTES.ROW_CLASS ,'="', rowId, '" ', ATTRIBUTES.COLUMN_CLASS,'="', colId, '" >',
				icon,
				// (edge case) Truncate labels to 500 characters. Limit of 500 already used in modelling/DataPreviewView.js
				// Browsers can have issues displaying really huge strings (100,000+ characters). See bug 8461.
				`<div class="list-item-label" ${styleString}>\
					<div class="list-item-text" ${textColor}>${_.escape(label.substr(0, 500))}</div>\
				</div>`,
				'</div>'
			].join('');
		},

		/**
		 * Process the rendered items in the list and highlight any selected ones.
		 * Each entry in the list has a data-tuple attribute.
		 */
		renderSelected: function (/*renderInfo*/) {
			const selections = this.getController();
			if (selections) {
				var items = $(this.el).find('.item');
				_.each(items, function (item) {
					let tuple = null;
					$(item).removeClass('selected');
					try {
						var decodedTuple =decodeURIComponent(item.getAttribute('data-tuple'));
						tuple = JSON.parse(decodedTuple);
					} catch (err) {
						if (this.logger) {
							this.logger.warn('Unable to parse the string into a JSON object in the indented list.', decodedTuple);
						}
					}
					if (this.isSubsetOfTupleSelected(tuple, selections)) {
						$(item).addClass('selected');
					}
				}.bind(this));
			}
		},

		renderFocused: function (bShow) {
			//Clear first
			$(this.el).find('.item.focused').removeClass('focused');
			if (bShow && ($(this._focusedNode).hasClass('selected') || $(this._focusedNode).is(':focus'))) {
				$(this._focusedNode).addClass('focused');
			} else {
				$(this._focusedNode).blur();
				this._focusedNode = null;
			}
		},

		/**
		 * @returns true if a subset of this tuple is selected.
		 *          A tuple is a list of columnId/value pairs stored as the "data-tuple" attribute
		 *          for row rendered in the list  {"5":"Canada", "2":"Backpacks"}
		 *
		 *          A subset is selected if:
		 *          1) the last entry in the tuple (in field order) is selected
		 *          2) preceding entries either have selections that match the tuple column/value
		 *                                   OR have NO selections for their columns
		 */
		isSubsetOfTupleSelected: function(tuple, selections) {
			if(!tuple || (!this.isDataFiltersEnabled() && !selections)) {
				return false;
			}

			var lastInTupleSelected=false;
			var tupleKeys=_.keys(tuple);
			for(var i=0; i<tupleKeys.length; ++i) {
				var columnId=tupleKeys[i];
				if (tuple[columnId]!==undefined) {
					if (this.isDataFiltersEnabled()) {
						const selectedPoints = this._dataPointSelections.getSelections([this.columnIdToDataItemMap[columnId]], /*compact form*/true);
						const isItemSelected = (selectedPoints && selectedPoints.length > 0);
						const isValueSelected = selectedPoints && selectedPoints.some((selectedPoint) => (selectedPoint[0].value === tuple[columnId].value));
						if (isItemSelected && !isValueSelected) {
							return false;
						}
						lastInTupleSelected = isValueSelected;
					} else {
						if (selections.isItemSelected(columnId) && !selections.isValueSelected(columnId, EventUtils.toDeprecatedPayload(tuple[columnId]))) {
							return false;
						}
						lastInTupleSelected = selections.isValueSelected(columnId, EventUtils.toDeprecatedPayload(tuple[columnId]));
					}
				}
			}
			return lastInTupleSelected;
		},


		/**
		 * Handle the on container entered event
		 * Sets up keyboard navigation inside the list
		 */
		onEnterContainer: function () {

			// get list items and through and set tabindex
			var oItems = $(this.el).find('.item');

			// place focus on the first item
			if (oItems.length > 0) {
				if (this._focusedNode) {
					this._focusedNode.focus();
				} else {
					oItems[0].focus();
				}
			}
		},

		/**
		 * Handle keyboard events
		 * @param event - keyboard event object
		 */
		onDataItemKeydown: function (event) {

			// process various keyboard events
			switch (event.keyCode) {
				case 37:
				case 38:
					this._moveFocusUp();
					break;
				case 39:
				case 40:
					this._moveFocusDown();
					break;
				case 32:
				case 13:
				// pass to the click handler for item selection
					this.onDataItemClick(event);
					// prevent widget move/resize/etc when clicked on item.
					event.stopPropagation();
					break;
				default:
					return;
			}
			event.preventDefault();
		},

		/**
		 * Returns the index of list item that has focus
		 * @return integer
		 * @private
		 */
		_getFocusedItemIndex: function () {
			var oItems = $(this.el).find('.item');
			var iFocusIndex = 0;
			for (var i = 0; i < oItems.length; i++) {
				if ($(oItems[i]).is(':focus')) {
					iFocusIndex = i;
					break;
				}
			}
			return iFocusIndex;
		},

		/**
		 * Sets the focus on the list item that is rendered above the currently focussed item (stops at top)
		 * @private
		 */
		_moveFocusUp: function () {
			// get current index
			var iFocusIndex = this._getFocusedItemIndex();

			// set focus on item
			this._setItemFocus(Math.max((iFocusIndex - 1), 0));
		},

		/**
		 * Sets the focus on the list item that is rendered below the currently focussed item (stops at bottom)
		 * @private
		 */
		_moveFocusDown: function () {
			// get current index
			var iFocusIndex = this._getFocusedItemIndex();

			// set focus on item
			this._setItemFocus(Math.min((iFocusIndex + 1), ($(this.el).find('.item').length - 1)));
		},

		/**
		 * Sets the focus on the list item at the given index
		 * @param iIndex:	List item index to set focus
		 * @private
		 */
		_setItemFocus: function (iIndex) {
			$(this.el).find('.item')[iIndex].focus();
		},

		/**
		 * Indented List view overwrites the default animation in order to avoid it fading in/out when
		 * applying filters.
		 */
		//@Override
		animate: function () {
			this.visModel.renderCompleteBeforeAnimation(); // Rendering is complete, no animation yet
		},

		_isOnScrollBar: function (event) {
			return this.scrollBarUtil.isOnScrollBar(event);
		}
	});

	return IndentedListView;
});
